import { z } from "zod";
import { ParseResult, ok, err } from "./result";

export const REGISTRY_SPEC_VERSION = 1;

const slug = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "must be a lowercase slug (a-z, 0-9, dashes)");

const semver = z
  .string()
  .regex(/^\d+\.\d+\.\d+$/, "must be a semver version (e.g. 1.0.0)");

export const RegistryEntrySchema = z.object({
  id: slug,
  title: z.string().min(1, "title is required"),
  version: semver,
  icon: z.string().url("icon must be a URL").optional(),
  manifestUrl: z.string().url("manifestUrl must be a URL"),
  tags: z.array(z.string()).optional().default([]),
  access: z.enum(["free", "paid"]).optional().default("free"),
});
export type RegistryEntry = z.infer<typeof RegistryEntrySchema>;

export const RegistrySchema = z
  .object({
    specVersion: z.literal(REGISTRY_SPEC_VERSION),
    name: z.string().min(1, "registry name is required"),
    packs: z.array(RegistryEntrySchema),
  })
  .superRefine((r, ctx) => {
    const seen = new Set<string>();
    r.packs.forEach((pack, i) => {
      if (seen.has(pack.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate pack id: "${pack.id}"`,
          path: ["packs", i, "id"],
        });
      }
      seen.add(pack.id);
    });
  });
export type Registry = z.infer<typeof RegistrySchema>;

function formatIssue(issue: z.ZodIssue): string {
  const path = issue.path.join(".");
  return path ? `${path}: ${issue.message}` : issue.message;
}

export function parseRegistry(input: unknown): ParseResult<Registry> {
  if (input && typeof input === "object" && "specVersion" in input) {
    const sv = (input as { specVersion: unknown }).specVersion;
    if (typeof sv === "number" && sv > REGISTRY_SPEC_VERSION) {
      return err([
        `This registry requires a newer app version (specVersion ${sv}; this app supports ${REGISTRY_SPEC_VERSION}). Please update the app.`,
      ]);
    }
  }
  const result = RegistrySchema.safeParse(input);
  if (result.success) return ok(result.data);
  return err(result.error.issues.map(formatIssue));
}

export function parseRegistryJson(text: string): ParseResult<Registry> {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return err(["registry.json is not valid JSON"]);
  }
  return parseRegistry(data);
}
